import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const FEATURED_SERVICES = [
  {
    title: 'Full House Design',
    tagline: 'Complete Home Transformation',
    description: 'Comprehensive top-to-bottom transformations that deliver a cohesive, timeless living environment — every room curated to reflect your taste and protect your investment.',
    image: 'https://lisamerkledesign.com/wp-content/uploads/2022/12/211.png',
    alt: 'Full house luxury interior design Beverly Hills',
  },
  {
    title: 'Renovation Consultation',
    tagline: 'Expert Project Guidance',
    description: 'Expert guidance through complex renovations — from scope definition and budget planning to contractor coordination — helping you avoid costly mistakes at every stage.',
    image: 'https://lisamerkledesign.com/wp-content/uploads/2022/12/236711611_132640305703782_9042178330528733820_n.jpg',
    alt: 'Luxury renovation consultation Beverly Hills Los Angeles',
  },
  {
    title: 'Kitchen & Bathroom Design',
    tagline: 'Functional Luxury Spaces',
    description: 'The rooms that define a home\'s value. We design functional luxury kitchens and spa-inspired bathrooms with timeless materials, architectural precision, and exceptional craft.',
    image: 'https://lisamerkledesign.com/wp-content/uploads/2022/12/314729600_962445298048832_6610108580665789119_n.jpg',
    alt: 'Luxury kitchen and bathroom design Los Angeles',
  },
];

export default function ServicesDetailSection() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => setTimeout(() => node.classList.add('revealed'), i * 120));
        obs.disconnect();
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-cream-warm py-10">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="reveal text-center mb-16">
          <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Featured Services</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-charcoal mt-3 leading-tight">
            Signature Design Services
          </h2>
        </div>

        <div className="flex flex-col gap-0 border border-stone/20">
          {FEATURED_SERVICES.map((service, i) => (
            <div
              key={service.title}
              className={`reveal grid lg:grid-cols-2 min-h-[500px] ${i > 0 ? 'border-t border-stone/15' : ''}`}
            >
              {/* Image */}
              <div className={`overflow-hidden img-zoom relative ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img
                  src={service.image}
                  alt={service.alt}
                  className="w-full h-full object-cover object-top min-h-[320px]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 to-transparent" />
              </div>
              {/* Content */}
              <div className={`flex flex-col justify-center p-12 lg:p-16 ${i % 2 === 1 ? 'lg:order-1 bg-white' : 'bg-charcoal'}`}>
                <div className={`text-[10px] tracking-[0.25em] uppercase font-sans mb-3 ${i % 2 === 1 ? 'text-stone' : 'text-stone/60'}`}>
                  {service.tagline}
                </div>
                <span className={`font-display text-7xl font-light mb-2 leading-none ${i % 2 === 1 ? 'text-stone/15' : 'text-cream/10'}`}>
                  0{i + 1}
                </span>
                <h3 className={`font-display text-3xl md:text-4xl font-light mb-5 leading-tight ${i % 2 === 1 ? 'text-charcoal' : 'text-cream'}`}>
                  {service.title}
                </h3>
                <div className={`w-8 h-px mb-6 ${i % 2 === 1 ? 'bg-stone' : 'bg-stone/50'}`} />
                <p className={`text-sm leading-relaxed font-sans mb-8 ${i % 2 === 1 ? 'text-charcoal/60' : 'text-cream/55'}`}>
                  {service.description}
                </p>
                <Link
                  to="/location#contact"
                  className={`inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-sans cursor-pointer self-start hover:gap-4 transition-all duration-300 ${
                    i % 2 === 1
                      ? 'text-charcoal border-b border-charcoal/25 pb-0.5 hover:border-charcoal'
                      : 'text-cream/60 border-b border-cream/20 pb-0.5 hover:text-cream hover:border-cream'
                  }`}
                >
                  Inquire About This Service <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
